import {
  Box,
  Checkbox,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useCallback, useMemo } from "react";

import { BucketKey } from "components/buckets/BucketToggle";
import { bucketSeriesSelectStyles } from "styles/ChartsReport.styles";
import {
  ALL_SECURITIES,
  ONLY_STOCKS,
  BucketSelection,
} from "./chartSeriesSelection";

// Re-exported at the surface the drop-down's own callers already import from.
// Their single home is chartSeriesSelection, which also titles the lines with
// them.
export { ALL_SECURITIES, ONLY_STOCKS };

/**
 * The sentinels are what let ONE MUI multi-select carry all three parts of a
 * BucketSelection: "All Securities" and "Only Stocks" are options the reader
 * ticks like any other, but neither is a security, so each travels as a value
 * that no symbol can collide with (a symbol is a ticker — it has no spaces).
 */
const isAll = (value: string) => value === ALL_SECURITIES;
const isStocks = (value: string) => value === ONLY_STOCKS;

/**
 * One control can carry SEVERAL buckets, so a row's value has to name its
 * bucket as well as its entry: "All Securities" is offered once per bucket,
 * and two MenuItems sharing a value are ONE option as far as MUI is concerned.
 *
 * Decoded by POSITION rather than by splitting on the separator. A BucketKey
 * is exactly one character, so the bucket is the first character and the entry
 * is everything past it — which holds however odd the symbol is, and a symbol
 * arrives from a plain 100-character CharField with no validator, so it may
 * well contain a colon of its own.
 */
const optionValue = (bucketKey: BucketKey, entry: string) =>
  `${bucketKey}:${entry}`;
const valueBucket = (value: string) => value.slice(0, 1) as BucketKey;
const valueEntry = (value: string) => value.slice(2);

/** One bucket's block of rows inside a control. */
export interface BucketSeriesSection {
  bucketKey: BucketKey;
  /**
   * Prefixes every one of this block's option labels, so a row still names
   * its bucket once several buckets share a menu. Empty for a control that
   * carries one bucket and titles it above the field instead.
   */
  prefix: string;
  /** Every security this bucket offers, sorted. Empty for Bucket 3. */
  securities: string[];
  /**
   * Whether this run gave the bucket a stock-only series, which is what
   * decides whether its Only Stocks row is offered at all (hasStocksSeries).
   * Bucket 1 alone, and never asserted from the bucket number: the payload is
   * what says which bucket has one.
   */
  offersStocks: boolean;
  /** The securities currently picked, already pruned to `securities`. */
  selectedSecurities: string[];
  selection: BucketSelection;
  /** This bucket produced no data in the last run, so there is nothing to plot. */
  disabled: boolean;
}

interface BucketSeriesSelectProps {
  /** The name shown above the field. */
  label: string;
  /** Distinguishes this control's label element from the others' on the row. */
  controlId: string;
  /** The buckets this control carries, in menu order. */
  sections: BucketSeriesSection[];
  onChange: (bucketKey: BucketKey, next: BucketSelection) => void;
}

/**
 * What a section is currently SHOWING, which is not always what its selection
 * is holding: the securities are pruned to the current run, and a disabled
 * bucket is shown empty while the real selection stays held.
 */
const displayedSelection = (section: BucketSeriesSection): BucketSelection => ({
  all: section.selection.all,
  // Pruned the way the securities beside it are, and for the same reason: a
  // selection outlives the run it was made against, so a block whose run
  // offers no stock-only series is SHOWING that row unticked whatever the
  // held selection says.
  stocks: section.offersStocks && section.selection.stocks,
  symbols: section.selectedSecurities,
});

const sameSelection = (a: BucketSelection, b: BucketSelection) =>
  a.all === b.all &&
  a.stocks === b.stocks &&
  a.symbols.length === b.symbols.length &&
  a.symbols.every((symbol, index) => symbol === b.symbols[index]);

/**
 * Where a section's LAST whole-bucket row carries its rule — the Only Stocks
 * row where the run offers one, the All Securities row where it does not.
 *
 * The rule belongs under the GROUP of whole-bucket rows rather than to one row
 * of it, which is why it moves: All Securities and Only Stocks are both
 * aggregates over the whole bucket, and the line separates those from the
 * individual securities under them.
 *
 * It rules off the securities BELOW it when the bucket has any; with none, the
 * group is the whole section and takes the heavier rule that ends a section
 * instead — but only when another section follows, rather than left ruling off
 * nothing at the foot of the menu.
 */
const lastAggregateRowRule = (
  hasSecurities: boolean,
  followedByAnother: boolean,
) => {
  if (hasSecurities) return bucketSeriesSelectStyles.ruledOff;
  if (followedByAnother) return bucketSeriesSelectStyles.sectionEnd;
  return undefined;
};

/**
 * A series drop-down over one or more buckets: each bucket's All Securities
 * first — and, where the run carries one, its Only Stocks row beneath that —
 * ruled off from that bucket's individual securities below, and the buckets
 * themselves ruled off from each other by a heavier line.
 *
 * Every row is independent — All Securities, Only Stocks and any number of
 * the securities under them can be ticked together, and the chart draws a
 * line for each. The
 * bucket's own line is a weighted return over the whole bucket and not the
 * sum of the lines beside it, so it is told apart by being NAMED apart
 * ("Bucket 2: All Securities") and by keeping the bucket's own accent, rather
 * than by being kept off the chart while a security is picked. Only Stocks is
 * that same whole-bucket aggregate measured on the equity legs alone, so it is
 * named and stroked apart on exactly those terms.
 *
 * A bucket can reach here with no securities at all — Bucket 3, which
 * carries no series to draw, and a pair bucket whose every member report
 * failed — so its block is the All Securities row alone.
 *
 * A bucket this run does not carry has its own rows GREYED rather than the
 * whole control: a control carrying two buckets must still offer the one that
 * did produce data. The control itself is disabled only when every bucket in
 * it is.
 */
const BucketSeriesSelect = ({
  label,
  controlId,
  sections,
  onChange,
}: BucketSeriesSelectProps) => {
  const labelId = `${controlId}-label`;

  const value = useMemo(
    () =>
      sections.flatMap((section) => [
        ...(section.selection.all
          ? [optionValue(section.bucketKey, ALL_SECURITIES)]
          : []),
        ...(section.offersStocks && section.selection.stocks
          ? [optionValue(section.bucketKey, ONLY_STOCKS)]
          : []),
        ...section.selectedSecurities.map((symbol) =>
          optionValue(section.bucketKey, symbol),
        ),
      ]),
    [sections],
  );

  // What each row's value reads as, so the closed control can name the
  // selection in the same words the menu offered it in.
  const optionLabels = useMemo(() => {
    const labels = new Map<string, string>();
    for (const section of sections) {
      labels.set(
        optionValue(section.bucketKey, ALL_SECURITIES),
        `${section.prefix}${ALL_SECURITIES}`,
      );
      if (section.offersStocks) {
        labels.set(
          optionValue(section.bucketKey, ONLY_STOCKS),
          `${section.prefix}${ONLY_STOCKS}`,
        );
      }
      for (const symbol of section.securities) {
        labels.set(
          optionValue(section.bucketKey, symbol),
          `${section.prefix}${symbol}`,
        );
      }
    }
    return labels;
  }, [sections]);

  const handleChange = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      // MUI hands a string back only for a single-value Select; this one is
      // always `multiple`, so the array is the real shape and the branch is
      // narrowing rather than a case that happens.
      const picked =
        typeof event.target.value === "string"
          ? event.target.value.split(",")
          : event.target.value;
      for (const section of sections) {
        const entries = picked
          .filter((entry) => valueBucket(entry) === section.bucketKey)
          .map(valueEntry);
        const next: BucketSelection = {
          all: entries.some(isAll),
          stocks: entries.some(isStocks),
          symbols: entries.filter(
            (entry) => !isAll(entry) && !isStocks(entry),
          ),
        };
        // Only the block the reader actually clicked is written back. Every
        // other block would round-trip what it is SHOWING, which is pruned to
        // this run and empty while its bucket is disabled — so writing it back
        // would drop a symbol the selection is holding for the next run, and a
        // click on Bucket 3 would quietly rewrite Bucket 2.
        if (!sameSelection(next, displayedSelection(section))) {
          onChange(section.bucketKey, next);
        }
      }
    },
    [onChange, sections],
  );

  // Everything picked, in the order the menu offers it. All Securities no
  // longer stands for the others, so collapsing to it alone would under-report
  // the control: it would read "All Securities" while two more lines of its
  // own are on the chart.
  const renderValue = useCallback(
    (picked: string[]) =>
      picked.length === 0
        ? "None"
        : picked.map((entry) => optionLabels.get(entry) ?? entry).join(", "),
    [optionLabels],
  );

  return (
    // The control's name sits ABOVE it rather than in it. A MUI InputLabel
    // only shrinks out of the way when the field has a value, and
    // `displayEmpty` means this one always renders one ("None") — so the two
    // drew on top of each other. A label above also matches how the report
    // settings beside this row label their own controls.
    <Box sx={bucketSeriesSelectStyles.field}>
      <Typography id={labelId} sx={bucketSeriesSelectStyles.label}>
        {label}
      </Typography>
      <Select
        multiple
        displayEmpty
        size="small"
        disabled={sections.every((section) => section.disabled)}
        labelId={labelId}
        value={value}
        onChange={handleChange}
        renderValue={renderValue}
        sx={bucketSeriesSelectStyles.control}
      >
        {/*
          Every rule in this menu — the one splitting All Securities off from
          the securities below it, and the one splitting one bucket off from
          the next — is a BORDER on a row rather than a <Divider> between two.
          Select clones every element child of its listbox with role="option"
          and offers no way out of it, so a Divider there is announced to a
          screen reader as an extra, empty, selectable option. A border draws
          the same line and adds nothing to the list.
        */}
        {sections.flatMap((section, index) => {
          const followedByAnother = index < sections.length - 1;
          const lastSecurity = section.securities.length - 1;
          return [
            <MenuItem
              key={optionValue(section.bucketKey, ALL_SECURITIES)}
              value={optionValue(section.bucketKey, ALL_SECURITIES)}
              disabled={section.disabled}
              sx={
                section.offersStocks
                  ? undefined
                  : lastAggregateRowRule(
                      section.securities.length > 0,
                      followedByAnother,
                    )
              }
            >
              <Checkbox checked={section.selection.all} size="small" />
              <ListItemText primary={`${section.prefix}${ALL_SECURITIES}`} />
            </MenuItem>,
            ...(section.offersStocks
              ? [
                  <MenuItem
                    key={optionValue(section.bucketKey, ONLY_STOCKS)}
                    value={optionValue(section.bucketKey, ONLY_STOCKS)}
                    disabled={section.disabled}
                    sx={lastAggregateRowRule(
                      section.securities.length > 0,
                      followedByAnother,
                    )}
                  >
                    <Checkbox
                      checked={section.selection.stocks}
                      size="small"
                    />
                    <ListItemText
                      primary={`${section.prefix}${ONLY_STOCKS}`}
                    />
                  </MenuItem>,
                ]
              : []),
            ...section.securities.map((symbol, symbolIndex) => (
              <MenuItem
                key={optionValue(section.bucketKey, symbol)}
                value={optionValue(section.bucketKey, symbol)}
                disabled={section.disabled}
                sx={
                  followedByAnother && symbolIndex === lastSecurity
                    ? bucketSeriesSelectStyles.sectionEnd
                    : undefined
                }
              >
                <Checkbox
                  checked={section.selectedSecurities.includes(symbol)}
                  size="small"
                />
                <ListItemText primary={`${section.prefix}${symbol}`} />
              </MenuItem>
            )),
          ];
        })}
      </Select>
    </Box>
  );
};

export default BucketSeriesSelect;
